// React
import { createContext, MutableRefObject, ReactNode, useContext, useMemo, useState } from "react";
// MUI
import { GridRowId, useGridApiRef } from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";

//Types
import { IDeal } from "@Common/types/MyCit/model";

interface IAllDealsContext {
	filteredAllDeals: GridRowId[];
	setFilteredAllDeals: (value: GridRowId[]) => void;
	apiRef: MutableRefObject<GridApiPremium>;
}

const AllDealsContext = createContext<IAllDealsContext>({
	filteredAllDeals: [],
	setFilteredAllDeals: () => {},
	apiRef: { current: {} as GridApiPremium },
});

interface IAllDealsContextProviderProps {
	children: ReactNode;
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	allDeals: IDeal[];
}

const AllDealsContextProvider = (props: Readonly<IAllDealsContextProviderProps>) => {
	const { children, locationId, commissionCycleId, payPeriodId, allDeals } = props;

	const apiRef = useGridApiRef();

	// States
	const [filteredAllDeals, setFilteredAllDeals] = useState<GridRowId[]>([]);

	// This is what the Context is providing to be used in the children components
	const value = useMemo(
		() => ({
			apiRef,
			filteredAllDeals,
			setFilteredAllDeals,
		}),
		[allDeals, filteredAllDeals, locationId, commissionCycleId, payPeriodId]
	);

	return <AllDealsContext.Provider value={value}>{children}</AllDealsContext.Provider>;
};

function useAllDealsContextData() {
	const context = useContext(AllDealsContext);
	if (!context) {
		throw new Error("useAllDealsContextData must be used within a AllDealsContextProvider");
	}
	return context;
}

export { AllDealsContextProvider, useAllDealsContextData };
